import type { StoreApi } from 'zustand'
import type { AppState } from '@/store/rootTypes'
import {
  completeGraduationAssessmentSession,
  createGraduationAssessmentSession,
  invalidateGraduationAssessmentSession,
  submitGraduationAssessmentResponse,
} from '@/entities/graduation-assessment'
import { generateLearningReport } from '@/entities/report'
import type { WrongQuestion } from '@/types'
import { addArchivedCount, appendWithRetention, DATA_RETENTION_LIMITS } from '@/store/retention'
import { activeWorkspacePatch } from '@/store/workspace'
import { projectWrongQuestionUpdates } from '@/entities/wrong-question'
import { createGraduationRewardClaimId } from '@/entities/reward-claim'
import { completeRemediationCertification } from '@/entities/remediation-certification'
import { isQualifiedEvidence } from '@/entities/attempt'
import { createLearningTaskCompletionKey } from '@/entities/progress'
import { projectLearningAttemptPatch, resolveLearningAttemptReplay } from '@/store/slices/learningSlice'

type SetState = StoreApi<AppState>['setState']
type GetState = StoreApi<AppState>['getState']

const ASSESSMENT_SESSION_LIMIT = 500

function retainMany<T>(current: readonly T[], added: readonly T[], limit: number) {
  const combined = [...current, ...added]
  const archivedCount = Math.max(0, combined.length - limit)
  return {
    items: archivedCount > 0 ? combined.slice(archivedCount) : combined,
    archivedCount,
  }
}

function sameWrongItem(left: WrongQuestion, right: WrongQuestion): boolean {
  return left.studentId === right.studentId
    && left.learningStageId === right.learningStageId
    && left.unitId === right.unitId
    && left.originalQuestionId === right.originalQuestionId
}

function projectAddedWrongQuestion(
  current: WrongQuestion[],
  archiveStats: AppState['archiveStats'],
  question: WrongQuestion,
) {
  const existing = current.find((item) => sameWrongItem(item, question))
  if (existing) {
    const incomingAttempts = question.variantAttempts.filter((incoming) => (
      !existing.variantAttempts.some((attempt) => attempt.attemptId === incoming.attemptId)
    ))
    if (incomingAttempts.length === 0) return { wrongQuestions: current, archiveStats }
    return {
      wrongQuestions: current.map((item) => item.id === existing.id
        ? { ...item, variantAttempts: [...item.variantAttempts, ...incomingAttempts] }
        : item),
      archiveStats,
    }
  }
  const retained = appendWithRetention(current, question, DATA_RETENTION_LIMITS.wrongQuestions)
  return {
    wrongQuestions: retained.items,
    archiveStats: addArchivedCount(archiveStats, 'wrongQuestions', retained.archivedCount),
  }
}

function projectWrongQuestions(
  current: readonly WrongQuestion[],
  session: ReturnType<typeof completeGraduationAssessmentSession>,
): WrongQuestion[] {
  const projected = [...current]
  for (const response of session.responses) {
    if (response.attempt.correct || !isQualifiedEvidence(response.attempt)) continue
    const candidate: WrongQuestion = {
      id: `wq:${session.studentId}:${session.stageId}:${session.unitId}:${response.questionId}`,
      studentId: session.studentId,
      originalQuestionId: response.questionId,
      unitId: session.unitId,
      category: response.category,
      ability: response.attempt.ability,
      knowledgePointId: response.attempt.knowledgePointId,
      source: 'assessment',
      learningStageId: session.stageId,
      studentAnswer: response.attempt.studentAnswer ?? '',
      correctAnswer: response.attempt.correctAnswer ?? '',
      questionSnapshot: response.attempt.questionSnapshot,
      replayDescriptor: response.attempt.replayDescriptor,
      aiExplanation: `本题考查${response.category}。请先理解错误原因，再完成变式训练和独立认证。`,
      variantAttempts: [{
        attemptId: response.attempt.id,
        correct: false,
        score: response.attempt.score,
        completedAt: response.attempt.completedAt,
      }],
      remediationLoopCount: 0,
      masteryState: 'wrong',
      workflowState: 'idle',
      status: 'wrong',
      createdAt: new Date(response.attempt.completedAt),
    }
    const existingIndex = projected.findIndex((item) => sameWrongItem(item, candidate))
    if (existingIndex < 0) {
      projected.push(candidate)
      continue
    }
    const existing = projected[existingIndex]
    if (existing.variantAttempts.some((attempt) => attempt.attemptId === response.attempt.id)) continue
    projected[existingIndex] = {
      ...existing,
      masteryState: 'wrong',
      status: 'wrong',
      questionSnapshot: existing.questionSnapshot ?? candidate.questionSnapshot,
      replayDescriptor: existing.replayDescriptor ?? candidate.replayDescriptor,
      variantAttempts: [...existing.variantAttempts, candidate.variantAttempts[0]],
    }
  }
  return projected
}

function projectRemediationCertification(
  state: AppState,
  input: Parameters<AppState['completeRemediationCertification']>[0],
) {
  const wrongQuestion = state.wrongQuestions.find((item) => item.id === input.wrongQuestionId)
  if (!wrongQuestion) throw new Error(`Wrong question ${input.wrongQuestionId} was not found.`)
  const targetIds = new Set([input.wrongQuestionId, ...(input.relatedWrongQuestionIds ?? [])])
  const targetQuestions = state.wrongQuestions.filter((item) => targetIds.has(item.id))
  if (targetQuestions.length !== targetIds.size || targetQuestions.some((item) => (
    item.studentId !== wrongQuestion.studentId
    || item.learningStageId !== wrongQuestion.learningStageId
    || item.unitId !== wrongQuestion.unitId
    || item.knowledgePointId !== wrongQuestion.knowledgePointId
  ))) throw new Error('Related wrong questions do not belong to the same remediation case.')
  const existingIds = new Set(state.attempts.map((attempt) => attempt.id))
  const newAttempts = input.attempts.filter((attempt) => !existingIds.has(attempt.id))
  if (newAttempts.length !== input.attempts.length) {
    const duplicateIds = input.attempts.filter((attempt) => existingIds.has(attempt.id))
    if (duplicateIds.length === input.attempts.length) {
      return {
        result: {
          passed: (wrongQuestion.masteryState ?? wrongQuestion.status) === 'mastered',
          score: wrongQuestion.certificationScore ?? 0,
          threshold: wrongQuestion.certificationThreshold ?? (input.layer === 'A' ? 90 : input.layer === 'B' ? 80 : 70),
          wrongQuestionPatch: {},
          attempts: duplicateIds,
        },
        workspacePatch: null,
        targetIds,
        studentId: wrongQuestion.studentId,
      }
    }
    throw new Error('Certification round contains already persisted attempts.')
  }
  const result = completeRemediationCertification({
    wrongQuestion,
    attempts: input.attempts,
    certificationRoundId: input.certificationRoundId,
    layer: input.layer,
    authorizedQuestions: input.authorizedQuestions,
    completedAt: input.completedAt,
  })
  const retained = retainMany(state.attempts, result.attempts, DATA_RETENTION_LIMITS.attempts)
  const workspacePatch = {
    attempts: retained.items,
    wrongQuestions: state.wrongQuestions.map((item) => {
      if (!targetIds.has(item.id)) return item
      return {
        ...item,
        ...result.wrongQuestionPatch,
        variantAttempts: [
          ...item.variantAttempts,
          ...result.attempts.map((attempt) => ({
            attemptId: attempt.id,
            correct: attempt.correct,
            purpose: 'certification' as const,
            score: attempt.score,
            completedAt: attempt.completedAt,
          })),
        ],
      }
    }),
    currentReport: generateLearningReport(retained.items, wrongQuestion.studentId, {
      stageId: wrongQuestion.learningStageId,
      assessmentRecords: state.assessmentRecords,
    }),
    archiveStats: addArchivedCount(state.archiveStats, 'attempts', retained.archivedCount),
  }
  return { result, workspacePatch, targetIds, studentId: wrongQuestion.studentId }
}

export function createAssessmentSlice(set: SetState, get: GetState): Pick<AppState,
  'addWrongQuestion' | 'recordDiagnosticAssessmentAnswer' | 'updateWrongQuestion' | 'updateWrongQuestions' | 'resolveWrongQuestion' | 'completeRemediationCertification' | 'completeAITeacherCertification' |
  'startGraduationAssessmentSession' | 'submitGraduationAssessmentResponse' |
  'completeGraduationAssessmentSession' | 'invalidateGraduationAssessmentSession' |
  'markRewardClaimPlantGranted' | 'recordRewardClaimDeliveryFailure' |
  'completeRewardClaimDelivery' | 'recordAssessment'> {
  return {
    addWrongQuestion: (question) => set((state) => {
      const patch = projectAddedWrongQuestion(state.wrongQuestions, state.archiveStats, question)
      if (patch.wrongQuestions === state.wrongQuestions) return state
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    recordDiagnosticAssessmentAnswer: (attempt, wrongQuestion) => set((state) => {
      if (attempt.source !== 'assessment' || attempt.evidenceKind !== 'formative') {
        throw new Error('Diagnostic assessment answers must be formative assessment evidence.')
      }
      if (wrongQuestion && (
        attempt.correct
        ||
        wrongQuestion.studentId !== attempt.studentId
        || wrongQuestion.source !== 'assessment'
        || !wrongQuestion.variantAttempts.some((entry) => entry.attemptId === attempt.id)
      )) {
        throw new Error('Diagnostic wrong question does not belong to the submitted attempt.')
      }
      const replay = resolveLearningAttemptReplay(state, attempt, 'Diagnostic')
      if (replay.status === 'duplicate') {
        if (!wrongQuestion) return state
        const wrongQuestionProjection = projectAddedWrongQuestion(
          state.wrongQuestions,
          state.archiveStats,
          wrongQuestion,
        )
        if (wrongQuestionProjection.wrongQuestions === state.wrongQuestions) return state
        return {
          ...wrongQuestionProjection,
          ...activeWorkspacePatch(state, wrongQuestionProjection),
        }
      }
      const attemptProjection = projectLearningAttemptPatch(state, replay.attempt)
      const wrongQuestionProjection = wrongQuestion
        ? projectAddedWrongQuestion(
            attemptProjection.workspacePatch.wrongQuestions,
            attemptProjection.workspacePatch.archiveStats,
            wrongQuestion,
          )
        : {
            wrongQuestions: attemptProjection.workspacePatch.wrongQuestions,
            archiveStats: attemptProjection.workspacePatch.archiveStats,
          }
      const workspacePatch = {
        ...attemptProjection.workspacePatch,
        ...wrongQuestionProjection,
      }
      return {
        ...workspacePatch,
        pendingAchievements: attemptProjection.pendingAchievements,
        ...activeWorkspacePatch(state, workspacePatch),
      }
    }),
    updateWrongQuestion: (id, update) => get().updateWrongQuestions([id], update),
    updateWrongQuestions: (ids, update) => set((state) => {
      const wrongQuestions = projectWrongQuestionUpdates(state.wrongQuestions, ids, update)
      if (wrongQuestions === state.wrongQuestions) return state
      const patch = { wrongQuestions }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    resolveWrongQuestion: (id) => set((state) => {
      const patch = {
        wrongQuestions: state.wrongQuestions.map((question) => question.id === id
          ? { ...question, masteryState: 'corrected' as const, status: 'corrected' as const }
          : question),
      }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    completeRemediationCertification: (input) => {
      let result: ReturnType<AppState['completeRemediationCertification']>
      set((state) => {
        const projection = projectRemediationCertification(state, input)
        result = projection.result
        if (!projection.workspacePatch) return state
        return { ...projection.workspacePatch, ...activeWorkspacePatch(state, projection.workspacePatch) }
      })
      return result!
    },
    completeAITeacherCertification: (input) => {
      let result: ReturnType<AppState['completeRemediationCertification']>
      set((state) => {
        const projection = projectRemediationCertification(state, input.certification)
        result = projection.result
        if (input.session.studentId !== projection.studentId) {
          throw new Error('AI certification session cannot update another learner.')
        }
        const certificationQuestions = projection.workspacePatch?.wrongQuestions ?? state.wrongQuestions
        const wrongQuestions = input.wrongQuestionPatch
          ? projectWrongQuestionUpdates(certificationQuestions, [...projection.targetIds], input.wrongQuestionPatch)
          : certificationQuestions
        const workspacePatch = {
          ...(projection.workspacePatch ?? {}),
          wrongQuestions,
          aiTeacherSession: input.session,
        }
        return {
          ...workspacePatch,
          aiTeacherResult: input.session.result ?? null,
          aiTeacherFingerprint: input.session.wrongItemFingerprint,
          ...activeWorkspacePatch(state, workspacePatch),
        }
      })
      return result!
    },
    startGraduationAssessmentSession: (input) => {
      const existing = get().assessmentSessions.find((session) => (
        session.status === 'draft'
        && session.studentId === input.studentId
        && session.stageId === input.stageId
        && session.unitId === input.unitId
      ))
      if (existing) return existing
      const session = createGraduationAssessmentSession(input)
      set((state) => {
        const patch = {
          assessmentSessions: [...state.assessmentSessions, session].slice(-ASSESSMENT_SESSION_LIMIT),
        }
        return { ...patch, ...activeWorkspacePatch(state, patch) }
      })
      return session
    },
    submitGraduationAssessmentResponse: (sessionId, response) => set((state) => {
      const session = state.assessmentSessions.find((item) => item.id === sessionId)
      if (!session) throw new Error(`Graduation assessment session ${sessionId} was not found.`)
      const updated = submitGraduationAssessmentResponse(session, response)
      const patch = {
        assessmentSessions: state.assessmentSessions.map((item) => item.id === sessionId ? updated : item),
      }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    completeGraduationAssessmentSession: (sessionId, record, reward) => {
      let receipt: ReturnType<AppState['completeGraduationAssessmentSession']> = { completed: false, rewardEligible: false }
      set((state) => {
        const session = state.assessmentSessions.find((item) => item.id === sessionId)
        if (!session) throw new Error(`Graduation assessment session ${sessionId} was not found.`)
        if (session.status === 'completed') return state
        if (record.sessionId !== session.id || record.stageId !== session.stageId || record.unitId !== session.unitId) {
          throw new Error('Assessment record does not belong to the graduation assessment session.')
        }
        const completed = completeGraduationAssessmentSession(session, record.completedAt)
        const rewardClaimId = createGraduationRewardClaimId(session.studentId, session.stageId)
        const rewardEligible = !state.rewardClaims.some((claim) => claim.id === rewardClaimId)
          && !state.assessmentRecords.some((existing) => (
          existing.kind !== 'certification'
          && existing.stageId === session.stageId
        ))
        const newAttempts = completed.responses
          .map((response) => response.attempt)
          .filter((attempt) => !state.attempts.some((existing) => existing.id === attempt.id))
        const retainedAttempts = retainMany(state.attempts, newAttempts, DATA_RETENTION_LIMITS.attempts)
        const projectedWrongQuestions = projectWrongQuestions(state.wrongQuestions, completed)
        const retainedWrongQuestions = projectedWrongQuestions.length > DATA_RETENTION_LIMITS.wrongQuestions
          ? {
              items: projectedWrongQuestions.slice(-DATA_RETENTION_LIMITS.wrongQuestions),
              archivedCount: projectedWrongQuestions.length - DATA_RETENTION_LIMITS.wrongQuestions,
            }
          : { items: projectedWrongQuestions, archivedCount: 0 }
        const completedRecord = { ...record, completedAt: completed.completedAt as string }
        const retainedRecords = state.assessmentRecords.some((existing) => existing.sessionId === sessionId)
          ? { items: state.assessmentRecords, archivedCount: 0 }
          : appendWithRetention(state.assessmentRecords, completedRecord, DATA_RETENTION_LIMITS.assessmentRecords)
        let archiveStats = addArchivedCount(state.archiveStats, 'attempts', retainedAttempts.archivedCount)
        archiveStats = addArchivedCount(archiveStats, 'wrongQuestions', retainedWrongQuestions.archivedCount)
        archiveStats = addArchivedCount(archiveStats, 'assessmentRecords', retainedRecords.archivedCount)
        const assessmentSessions = state.assessmentSessions.map((item) => item.id === sessionId ? completed : item)
        const rewardCreatedAt = completed.completedAt as string
        const rewardClaims = rewardEligible && reward
          ? [...state.rewardClaims, {
              id: rewardClaimId,
              kind: 'graduation-assessment' as const,
              status: 'pending' as const,
              studentId: session.studentId,
              stageId: session.stageId,
              unitId: session.unitId,
              assessmentSessionId: session.id,
              stars: Math.max(0, reward.stars),
              plantExperience: Math.max(0, reward.plantExperience),
              starsGrantedAt: rewardCreatedAt,
              createdAt: rewardCreatedAt,
            }]
          : state.rewardClaims
        const stars = rewardEligible && reward ? state.stars + Math.max(0, reward.stars) : state.stars
        const student = stars === state.stars ? state.student : { ...state.student, totalStars: stars }
        const completionKey = record.routeTaskId
          ? createLearningTaskCompletionKey(session.stageId, record.routeTaskId)
          : null
        const completedTasks = completionKey
          ? { ...state.completedTasks, [completionKey]: true }
          : state.completedTasks
        const taskCompletedAt = completionKey
          ? { ...state.taskCompletedAt, [completionKey]: completed.completedAt as string }
          : state.taskCompletedAt
        const patch = {
          assessmentSessions,
          attempts: retainedAttempts.items,
          wrongQuestions: retainedWrongQuestions.items,
          assessmentRecords: retainedRecords.items,
          rewardClaims,
          stars,
          student,
          completedTasks,
          taskCompletedAt,
          currentReport: generateLearningReport(retainedAttempts.items, session.studentId, {
            stageId: session.stageId,
            assessmentRecords: retainedRecords.items,
          }),
          archiveStats,
          weeklyPlanActive: true,
          weeklyPlanStartDate: state.weeklyPlanStartDate ?? completed.startedAt,
        }
        receipt = {
          completed: true,
          rewardEligible,
          ...(rewardEligible && reward ? { rewardClaimId } : {}),
        }
        return { ...patch, ...activeWorkspacePatch(state, patch) }
      })
      return receipt
    },
    markRewardClaimPlantGranted: (claimId, completedAt = new Date().toISOString()) => set((state) => {
      const claim = state.rewardClaims.find((item) => item.id === claimId)
      if (!claim || claim.status === 'completed' || claim.plantExperienceGrantedAt) return state
      const patch = {
        rewardClaims: state.rewardClaims.map((item) => item.id === claimId
          ? {
              ...item,
              plantExperienceGrantedAt: completedAt,
            }
          : item),
      }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    recordRewardClaimDeliveryFailure: (input) => set((state) => {
      const claim = state.rewardClaims.find((item) => item.id === input.claimId)
      if (!claim || claim.status === 'completed') return state
      const patch = {
        rewardClaims: state.rewardClaims.map((item) => item.id === input.claimId
          ? {
              ...item,
              deliveryAttemptCount: (item.deliveryAttemptCount ?? 0) + 1,
              lastDeliveryAttemptAt: input.attemptedAt,
              lastDeliveryError: input.error,
              nextDeliveryAttemptAt: input.nextAttemptAt,
            }
          : item),
      }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    completeRewardClaimDelivery: (claimId, completedAt = new Date().toISOString()) => set((state) => {
      const claim = state.rewardClaims.find((item) => item.id === claimId)
      if (!claim || claim.status === 'completed') return state
      const patch = {
        rewardClaims: state.rewardClaims.map((item) => item.id === claimId
          ? {
              ...item,
              status: 'completed' as const,
              plantExperienceGrantedAt: item.plantExperienceGrantedAt ?? completedAt,
              lastDeliveryAttemptAt: completedAt,
              lastDeliveryError: undefined,
              nextDeliveryAttemptAt: undefined,
              completedAt,
            }
          : item),
      }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    invalidateGraduationAssessmentSession: (sessionId, input) => set((state) => {
      const session = state.assessmentSessions.find((item) => item.id === sessionId)
      if (!session) throw new Error(`Graduation assessment session ${sessionId} was not found.`)
      const invalidated = invalidateGraduationAssessmentSession(session, input)
      const patch = {
        assessmentSessions: state.assessmentSessions.map((item) => item.id === sessionId ? invalidated : item),
      }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
    recordAssessment: (record) => set((state) => {
      const retained = appendWithRetention(state.assessmentRecords, record, DATA_RETENTION_LIMITS.assessmentRecords)
      const archiveStats = addArchivedCount(state.archiveStats, 'assessmentRecords', retained.archivedCount)
      const patch = { assessmentRecords: retained.items, archiveStats }
      return { ...patch, ...activeWorkspacePatch(state, patch) }
    }),
  }
}
